import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Upload } from "lucide-react";

const COLORS = [
  { name: "navy", value: "#0B1F33" },
  { name: "gold", value: "#C49A5A" },
  { name: "emerald", value: "#1F4E3B" },
  { name: "terracotta", value: "#B05A32" },
];

const HEROES = [
  { name: "Maldives", url: "https://images.unsplash.com/photo-1514282401047-d79a71a590e8?auto=format&fit=crop&w=900&q=70" },
  { name: "Dubai", url: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=900&q=70" },
  { name: "Bali", url: "https://images.pexels.com/photos/27306499/pexels-photo-27306499.jpeg?auto=compress&cs=tinysrgb&w=900" },
];

export default function SimWebsiteBuilder() {
  const [brand, setBrand] = useState("Azure Horizons");
  const [color, setColor] = useState("#0B1F33");
  const [hero, setHero] = useState(0);
  const [saved, setSaved] = useState(false);

  const publish = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="sim-website-builder">
      <div className="rounded-2xl border border-sand bg-white p-5 sm:p-6">
        <p className="mb-4 flex items-center justify-between font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-stone2">
          Website branding · DMC admin
          <span className="rounded-full bg-parch px-2 py-0.5 text-[9px] normal-case tracking-normal">sample data</span>
        </p>
        <label className="mb-1 block text-xs font-semibold text-ink">Business name</label>
        <input
          value={brand}
          onChange={(e) => setBrand(e.target.value.slice(0, 26))}
          data-testid="sim-brand-name-input"
          className="mb-4 w-full rounded-lg border border-sand px-3.5 py-2.5 text-sm outline-none focus:border-terra"
        />
        <label className="mb-1 block text-xs font-semibold text-ink">Primary color</label>
        <div className="mb-4 flex gap-2">
          {COLORS.map((c) => (
            <button
              key={c.name}
              onClick={() => setColor(c.value)}
              data-testid={`sim-brand-color-${c.name}`}
              aria-label={c.name}
              className={`h-8 w-8 rounded-full border-2 transition-transform duration-300 hover:scale-110 ${color === c.value ? "border-ink" : "border-transparent"}`}
              style={{ backgroundColor: c.value }}
            />
          ))}
          <span className="ml-1 self-center font-mono text-xs text-stone2">{color}</span>
        </div>
        <label className="mb-1 block text-xs font-semibold text-ink">Hero image</label>
        <div className="mb-4 grid grid-cols-3 gap-2">
          {HEROES.map((h, i) => (
            <button
              key={h.name}
              onClick={() => setHero(i)}
              data-testid={`sim-brand-hero-${h.name.toLowerCase()}`}
              className={`overflow-hidden rounded-lg border-2 transition-all duration-300 ${hero === i ? "border-terra" : "border-transparent opacity-60 hover:opacity-100"}`}
            >
              <img src={h.url} alt={h.name} loading="lazy" className="h-14 w-full object-cover" />
            </button>
          ))}
        </div>
        <label className="mb-1 block text-xs font-semibold text-ink">Logo</label>
        <div className="mb-5 flex items-center gap-2 rounded-lg border border-dashed border-sand px-3.5 py-2.5 text-sm text-stone2">
          <Upload className="h-4 w-4" /> Upload logo <span className="font-mono text-[10px]">(uses your name in this demo)</span>
        </div>
        <button
          onClick={publish}
          data-testid="sim-brand-publish-btn"
          className="w-full rounded-full py-3 text-sm font-semibold text-paper transition-colors duration-300"
          style={{ backgroundColor: saved ? "#1F4E3B" : "#0B1F33" }}
        >
          {saved ? "✓ Published to your website" : "Save & Publish"}
        </button>
      </div>

      <motion.div layout className="overflow-hidden rounded-2xl border border-sand bg-white shadow-sm" data-testid="sim-brand-preview">
        <div className="flex items-center gap-2 border-b border-sand bg-parch px-4 py-2">
          <span className="flex gap-1">
            <i className="h-2 w-2 rounded-full bg-terra/70" /><i className="h-2 w-2 rounded-full bg-ink/20" /><i className="h-2 w-2 rounded-full bg-ink/20" />
          </span>
          <span className="mx-auto rounded-full bg-white px-3 py-0.5 font-mono text-[10px] text-stone2">www.yourdmc.com</span>
        </div>
        <div className="relative h-40 overflow-hidden">
          <motion.img key={hero} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
            src={HEROES[hero].url} alt="Customer website hero" loading="lazy" className="h-full w-full object-cover" />
          <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/60 to-transparent p-4">
            <motion.p key={brand} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="font-serif text-lg text-white">
              {brand || "Your DMC"}
            </motion.p>
            <p className="text-[11px] text-white/80">Handcrafted journeys by local experts</p>
          </div>
        </div>
        <div className="p-4">
          <motion.button layout className="rounded-full px-5 py-2 text-xs font-bold text-white transition-colors duration-500" style={{ backgroundColor: color }} data-testid="sim-brand-preview-cta">
            Plan My Trip
          </motion.button>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {["Maldives Escape", "Dubai Icons", "Bali Slow Travel"].map((p) => (
              <div key={p} className="rounded-lg border border-sand p-2">
                <div className="mb-1.5 h-1.5 w-3/4 rounded-full" style={{ backgroundColor: color, opacity: 0.25 }} />
                <p className="text-[10px] font-semibold text-ink">{p}</p>
                <p className="text-[9px] text-stone2">6 nights · from $1,499</p>
              </div>
            ))}
          </div>
        </div>
        {saved && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mx-4 mb-4 flex items-center gap-2 rounded-lg bg-emerald2/10 px-3 py-2 text-xs font-semibold text-emerald2">
            <Check className="h-4 w-4" /> Live — your brand, your domain, your website.
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
